import React, { Fragment, useState, useEffect } from 'react'
import { Grid, TextField } from '@mui/material'

interface CreatedDateFilterProps {
  startDateValue?: string
  finishDateValue?: string
  onChangeStartDate:
    | ((event: React.ChangeEvent<HTMLInputElement> | undefined | any) => void)
    | undefined
  onChangeFinishDate:
    | ((event: React.ChangeEvent<HTMLInputElement> | undefined | any) => void)
    | undefined
  validateStartDate?: (startDate: string, endDate: string) => string
  validateEndDate?: (startDate: string, endDate: string) => string
  resetDate?: boolean
  disabled?: boolean
}

const defaultDateValidation = (
  startDate: string,
  endDate: string,
  isStart: boolean
): string => {
  if (startDate === '' && isStart) return 'Debe ingresar Fecha Desde'
  if (endDate === '' && !isStart) return 'Debe ingresar Fecha Hasta'
  if (startDate > endDate)
    return isStart
      ? 'Debe ser menor o igual que Fecha Hasta'
      : 'Debe ser mayor o igual que Fecha Desde'
  return ''
}

const getLast7DaysRange = (): { startDate: string; endDate: string } => {
  const today = new Date()
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(today.getDate() - 7)

  const formatDate = (date: Date) => date.toISOString().split('T')[0]

  return {
    startDate: formatDate(sevenDaysAgo),
    endDate: formatDate(today)
  }
}

export const CreatedDateFilterComponent: React.FC<CreatedDateFilterProps> = ({
  startDateValue,
  finishDateValue,
  onChangeFinishDate,
  onChangeStartDate,
  validateStartDate = defaultDateValidation,
  validateEndDate = defaultDateValidation,
  resetDate,
  disabled = false
}) => {
  const [startDate, setStartDate] = useState(startDateValue || '')
  const [finishDate, setFinishDate] = useState(finishDateValue || '')

  useEffect(() => {
    if (!startDateValue && !finishDateValue) {
      const { startDate, endDate } = getLast7DaysRange()
      setStartDate(startDate)
      setFinishDate(endDate)
    }
  }, [startDateValue, finishDateValue])

  useEffect(() => {
    const { startDate, endDate } = getLast7DaysRange()
    setStartDate(startDate)
    setFinishDate(endDate)
  }, [resetDate])

  const startDateError = validateStartDate(startDate, finishDate, true)
  const finishDateError = validateEndDate(startDate, finishDate, false)

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStartDate(event.target.value)
    if (onChangeStartDate) onChangeStartDate(event)
  }

  const handleFinishDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFinishDate(event.target.value)
    if (onChangeFinishDate) onChangeFinishDate(event)
  }

  useEffect(() => {
    if (disabled) {
      onChangeStartDate?.({
        target: { name: 'initialDate', value: undefined }
      } as React.ChangeEvent<{ name?: string; value: unknown }>)

      onChangeFinishDate?.({
        target: { name: 'endDate', value: undefined }
      } as React.ChangeEvent<{ name?: string; value: unknown }>)

      setStartDate('')
      setFinishDate('')
    } else {
      const { startDate, endDate } = getLast7DaysRange()
      setStartDate(startDate)
      setFinishDate(endDate)

      onChangeStartDate?.({
        target: { name: 'initialDate', value: startDate }
      } as React.ChangeEvent<{ name?: string; value: unknown }>)

      onChangeFinishDate?.({
        target: { name: 'endDate', value: endDate }
      } as React.ChangeEvent<{ name?: string; value: unknown }>)
    }
  }, [disabled])

  return (
    <Fragment>
      <Grid
        container
        direction='row'
        spacing={3}
        alignContent='center'
        alignItems='center'
      >
        <Grid item xs={12} sm={6}>
          <TextField
            id='initialDate'
            name='initialDate'
            label='Desde'
            type='date'
            value={startDate}
            onChange={handleStartDateChange}
            error={!!startDateError}
            helperText={startDateError}
            InputLabelProps={{
              shrink: true
            }}
            variant='outlined'
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            id='endDate'
            name='endDate'
            label='Hasta'
            type='date'
            value={finishDate}
            onChange={handleFinishDateChange}
            error={!!finishDateError}
            helperText={finishDateError}
            InputLabelProps={{
              shrink: true
            }}
            variant='outlined'
            fullWidth
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Fragment>
  )
}
